import { useEffect, useState } from "react";
import { api, currency } from "../lib/api";
import NestPie, { NEST_COLORS, CHART_GRID, CHART_AXIS, CHART_TOOLTIP } from "../components/NestChart";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const blankItem = { name: "", amount: "" };

export default function Debt() {
  const [snaps, setSnaps] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({
    date: new Date().toISOString().slice(0, 10),
    items: [{ ...blankItem }],
  });
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/debts").then((r) => setSnaps(r.data));

  useEffect(() => {
    load();
  }, []);

  const latest = snaps[snaps.length - 1];
  const totalNow = latest ? latest.items.reduce((s, i) => s + i.amount, 0) : 0;
  const firstSnap = snaps[0];
  const totalStart = firstSnap
    ? firstSnap.items.reduce((s, i) => s + i.amount, 0)
    : 0;
  const paidOff = totalStart - totalNow;

  const pieData =
    latest?.items.map((i) => ({ name: i.name, value: i.amount })) || [];

  const timelineData = snaps.map((s) => ({
    date: s.date,
    total: s.items.reduce((sum, i) => sum + i.amount, 0),
  }));

  const startNew = () => {
    setEditingId(null);
    setDraft({
      date: new Date().toISOString().slice(0, 10),
      items: latest
        ? latest.items.map((i) => ({ name: i.name, amount: i.amount }))
        : [{ ...blankItem }],
    });
    setShowForm(true);
  };

  const startEdit = (snap) => {
    setEditingId(snap.id);
    setDraft({
      date: snap.date,
      items: snap.items.map((i) => ({ name: i.name, amount: i.amount })),
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      date: draft.date,
      items: draft.items
        .filter((i) => i.name.trim())
        .map((i) => ({ name: i.name.trim(), amount: parseFloat(i.amount) || 0 })),
    };
    try {
      if (editingId) {
        await api.put(`/debts/${editingId}`, payload);
        toast.success("Snapshot updated");
      } else {
        await api.post("/debts", payload);
        toast.success("Snapshot added");
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/debts/${id}`);
    toast.success("Deleted");
    load();
  };

  const setItem = (idx, patch) => {
    const next = [...draft.items];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, items: next });
  };

  return (
    <div className="space-y-8" data-testid="debt-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-nest-muted">
          Progress
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-medium mt-2">
          Debt Tracker
        </h1>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="nest-card p-6">
          <div className="text-xs uppercase tracking-widest text-nest-muted">
            Current total
          </div>
          <div
            className="font-heading text-3xl tabular text-nest-warn mt-2"
            data-testid="debt-current-total"
          >
            {currency(totalNow)}
          </div>
          {latest && (
            <div className="text-xs text-nest-muted mt-1">as of {latest.date}</div>
          )}
        </div>
        <div className="nest-card p-6">
          <div className="text-xs uppercase tracking-widest text-nest-muted">
            Paid off so far
          </div>
          <div className="font-heading text-3xl tabular text-nest-good mt-2">
            {currency(paidOff)}
          </div>
          <div className="text-xs text-nest-muted mt-1">
            since {firstSnap?.date || "—"}
          </div>
        </div>
        <div className="nest-card p-6">
          <div className="text-xs uppercase tracking-widest text-nest-muted">
            Snapshots
          </div>
          <div className="font-heading text-3xl tabular mt-2">{snaps.length}</div>
          <div className="text-xs text-nest-muted mt-1">tracked over time</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="nest-card p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Current breakdown
          </div>
          <NestPie data={pieData} testId="debt-pie" height={280} />
        </div>
        <div className="nest-card p-6 lg:col-span-3">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Timeline
          </div>
          <div style={{ width: "100%", height: 280 }} data-testid="debt-timeline">
            <ResponsiveContainer>
              <LineChart data={timelineData}>
                <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke={CHART_AXIS} fontSize={11} />
                <YAxis stroke={CHART_AXIS} fontSize={11} tickFormatter={(v) => `£${v}`} />
                <Tooltip
                  formatter={(v) => currency(v)}
                  contentStyle={CHART_TOOLTIP}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke={NEST_COLORS[1]}
                  strokeWidth={2.5}
                  dot={{ fill: NEST_COLORS[1], r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="nest-card p-6" data-testid="debt-snapshots-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-nest-muted">
              Snapshots
            </div>
            <h3 className="font-heading text-xl mt-1">History</h3>
          </div>
          <button
            onClick={startNew}
            className="nest-btn-primary flex items-center gap-1 text-sm"
            data-testid="add-debt-snapshot-btn"
          >
            <Plus size={14} /> New snapshot
          </button>
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            className="border border-nest-line rounded-2xl p-5 mb-6 bg-nest-surface-1 space-y-4"
            data-testid="debt-form"
          >
            <div className="flex items-center gap-3">
              <label className="text-xs uppercase tracking-widest text-nest-muted">
                Date
              </label>
              <input
                type="date"
                className="nest-input"
                value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                data-testid="debt-date-input"
              />
            </div>
            <div className="space-y-2">
              {draft.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input
                    placeholder="Debt name"
                    className="nest-input col-span-6 text-sm"
                    value={it.name}
                    onChange={(e) => setItem(idx, { name: e.target.value })}
                  />
                  <input
                    placeholder="Amount"
                    type="number"
                    step="0.01"
                    className="nest-input col-span-4 text-sm tabular"
                    value={it.amount}
                    onChange={(e) => setItem(idx, { amount: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        items: draft.items.filter((_, i) => i !== idx),
                      })
                    }
                    className="col-span-2 text-nest-faint hover:text-nest-warn text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setDraft({ ...draft, items: [...draft.items, { ...blankItem }] })
                }
                className="text-sm text-nest-brand hover:underline"
              >
                + Add debt line
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="nest-btn-primary text-sm"
                data-testid="debt-submit-btn"
              >
                {editingId ? "Update" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="text-sm text-nest-muted hover:text-nest-ink"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="space-y-3" data-testid="debt-list">
          {snaps.length === 0 && (
            <div className="text-sm text-nest-faint py-6 text-center">
              No snapshots yet
            </div>
          )}
          {[...snaps].reverse().map((s) => {
            const t = s.items.reduce((sum, i) => sum + i.amount, 0);
            return (
              <div
                key={s.id}
                className="border border-nest-line rounded-2xl p-4 hover:border-nest-gold transition-colors"
                data-testid={`debt-snapshot-${s.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading text-lg">{s.date}</div>
                    <div className="text-xs text-nest-muted mt-1">
                      {s.items.length} debt{s.items.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="font-heading text-xl tabular text-nest-warn">
                      {currency(t)}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(s)}
                        className="text-nest-faint hover:text-nest-brand"
                      >
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => remove(s.id)}
                        className="text-nest-faint hover:text-nest-warn"
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
                {s.items.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.items.map((i, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-nest-surface-2 text-nest-soft px-3 py-1 rounded-full tabular"
                      >
                        {i.name} · {currency(i.amount)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
